<template>
  <div>
    <div class="Section PrefContent-defaultContent">
      <h2>Default Fields</h2>
      <div class="Notice Notice--info">
        <p class="Notice-copy">
          {{ copy.noticeDefault }}
        </p>
      </div>

      <div class="Section-grey">
        <div class="PrefContent-header">
          <div class="PrefContent-headerField">
            Field
          </div>
          <div class="PrefContent-headerCustomField">
            Visible Field Name
          </div>
        </div>
        <div
          v-for="field in subscribedFields"
          :key="field.id"
        >
          <div
            v-if="field.is_fixed === 1"
            class="PrefContent-list"
          >
            <div class="PrefContent-field">
              {{ field.default_name }}
            </div>
            <div class="PrefContent-rename">
              <input
                v-model="field.custom_name"
                placeholder="Edit the field name"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="Section">
      <h2>Additional Fields</h2>
      <div class="Notice Notice--info">
        <p class="Notice-copy">
          {{ copy.noticeAdditional }}
        </p>
      </div>
      <div class="Section-grey PrefContent-draggable">
        <div
          v-if="subscribedFields.length > 0"
          class="PrefContent-header"
        >
          <div class="PrefContent-headerField">
            Field
          </div>
          <div class="PrefContent-headerCustomField">
            Visible Field Name
          </div>
        </div>
        <draggable
          v-if="subscribedFields.length > 0"
          v-model="subscribedFields"
          :element="'div'"
          :move="changeOrder"
          class="PrefContent-dragContainer"
        >
          <transition-group name="ListDrag">
            <div
              v-for="field in subscribedFields"
              :key="field.id"
            >
              <div
                v-if="field.is_fixed === 0"
                class="PrefContent-list"
              >
                <div class="PrefContent-drag">
                  <span class="Icon Icon-bars" />
                </div>
                <div class="PrefContent-field">
                  {{ field.default_name }}
                </div>
                <div class="PrefContent-rename">
                  <input
                    v-model="field.custom_name"
                    placeholder="Edit the field name"
                  >
                </div>
                <div class="PrefContent-remove">
                  <a
                    href="#"
                    title="Remove field"
                    @click.prevent="deleteRow(field.id)"
                  >
                    <span class="WidgetDelete-icon Icon Icon-times-circle" />
                  </a>
                </div>
              </div>
            </div>
          </transition-group>
        </draggable>
        <div class="AddNewItem">
          <v-select
            v-model="dataForAutocomplete.selected"
            :debounce="250"
            :options="dataForAutocomplete.options"
            :on-change="addRow"
            :placeholder="copy.field.add"
            label="default_name"
          />
        </div>
        <div
          v-if="subscribedFields.length > subscribedFieldsDefaultData.length"
          class="Section-toolbar"
        >
          <a
            href=""
            class="Delete"
            title="Remove All"
            @click.prevent="deleteNotDefaultRows"
          ><i
            class="Icon Icon-trash"
          />Remove All</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueDraggable from 'vuedraggable';
import VueSelect from 'vue-select';
import CommonOperations from '../../../mixins/common-operations';
import LocalCopy from '../../../lang/en.json';

export default {
  components: {
    draggable: VueDraggable,
    'v-select': VueSelect,
  },
  mixins: [CommonOperations],
  props: {
    subscribedFieldsData: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
    autoCompleteGetAvailableFieldsUrl: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {
      subscribedFields: [],
      dataForAutocomplete: {
        selected: null,
        allOptions: [],
        options: [],
      },
      ui: {
        loading: false,
      },
    };
  },
  computed: {
    // Returns a list of default fields
    subscribedFieldsDefaultData() {
      return this.subscribedFields.filter((field) => {
        return field.is_fixed === 1;
      });
    },
    copy() {
      return LocalCopy.studentPreferenceCenter.subscribedFields;
    },
  },
  watch: {
    subscribedFields(val) {
      this.$emit('subscribedFieldsUpdated', val);
    },
  },
  created() {
    this.formatData(this.subscribedFieldsData);
    this.getInitialDataForAutocomplete();
    this.$bus.$on('updateDataAfterReset', (event) => {
      this.formatData(event.subscribedFieldsData);
    });
    this.$bus.$on('updateDataAfterSave', (event) => {
      this.formatData(event.subscribedFieldsData);
    });
  },
  methods: {
    formatData(data) {
      this.subscribedFields = data;
      Object.keys(this.subscribedFields).forEach((key) => {
        if (this.subscribedFields[key].custom_name === '') {
          this.subscribedFields[key].custom_name = this.subscribedFields[key].default_name;
        }
      });
    },

    // Method that is called when rows have been reordered by dragging
    changeOrder(evt) {
      return (evt.draggedContext.element.is_fixed !== 1);
    },

    // Method called when a field is removed
    deleteRow(rowId) {
      this.subscribedFields = this.subscribedFields.filter((field) => {
        return field.id !== rowId;
      });
      this.refreshOptions();
    },

    // Delete all the non-default fields from our object
    deleteNotDefaultRows() {
      this.subscribedFields = this.subscribedFieldsDefaultData;
      this.refreshOptions();
    },

    // Method called when a field is added to the fields table
    addRow(row) {
      if (row !== null) {
        this.subscribedFields.push(row);
        this.refreshOptions();
      }
    },

    // Gets all available fields to be used for 'Add a field' auto complete
    getSearchData() {
      this.dataForAutocomplete.allOptions = [];
      const getDataRequest = this.$_commonOperations_getDataNew(this.autoCompleteGetAvailableFieldsUrl, null);
      getDataRequest.then((response) => {
        const rows = response.data;
        rows.forEach((row) => {
          const data = {};
          Object.keys(row).forEach((key) => {
            data[key] = row[key];
          });
          this.dataForAutocomplete.allOptions.push(data);
        });
      })
        .catch(() => {
          this.$_commonOperations_emitMessage(LocalCopy.flashMessage.errors.genericError, 'error');
        });

      return getDataRequest;
    },

    // Builds the options to be used for 'Add a field' auto complete
    getInitialDataForAutocomplete() {
      this.ui.loading = true;
      this.getSearchData().then(() => {
        this.refreshOptions();
        this.ui.loading = false;
      });
    },

    // Method that updates the options list for autocomplete - it is called on init & every time we add/remove a row
    refreshOptions() {
      this.dataForAutocomplete.options = this.getAvailableOptions(this.dataForAutocomplete.allOptions, this.getRowIDs());
      // Forces select to clear the previously selected value
      if (document.getElementById('vue-student-preference-center').getElementsByClassName('clear').length) {
        document.getElementById('vue-student-preference-center').getElementsByClassName('clear')[0].click();
      }
    },

    // Gets a list of the field ids that are in use
    getRowIDs() {
      return Object.keys(this.subscribedFields).map((key) => {
        return this.subscribedFields[key].id;
      });
    },

    // Method that returns a list of available options to be used for autocomplete
    getAvailableOptions(allOptions, removeRowIds) {
      return allOptions.filter((val) => !removeRowIds.includes(val.id));
    },
  },
};
</script>
